/**
 * AdminPermissionsContext — single source of truth for the admin's
 * permission set, fetched from `/v1/admin/me` on mount.
 *
 * Why a context (not a hook directly):
 *   Most pages need 1+ permission checks; we want a single network
 *   request per session, refreshed on window focus after 5 minutes.
 *
 * Pattern:
 *   - Wrap the app under <AdminPermissionsProvider>.
 *   - Use `useAdminPermissions().hasPermission('admin.create')` in
 *     pages and components.
 *   - Use `<RequirePermission permission="…">` to gate routes.
 *
 * This file is the foundation every subsequent admin frontend PR depends
 * on (T1 PR-3, T2 PR-2, T4 PR-2 all consume hasPermission).
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-rbac-design.md §8.1.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useApiUrl } from '@refinedev/core';
import { adminFetch } from '@/providers/admin-api';

interface CurrentAdmin {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  last_login_at: string | null;
  role_keys: string[];
  primary_role_key: string;
  permissions: string[];
}

interface AdminPermissionsContextValue {
  /** True once the first /v1/admin/me fetch has resolved (success or failure). */
  loaded: boolean;
  /** The current admin profile, or null if not loaded / unauthenticated. */
  admin: CurrentAdmin | null;
  /** Returns true iff the admin holds the given permission key. */
  hasPermission: (key: string) => boolean;
  /** Force a refetch (e.g. after a self-role change). */
  refresh: () => Promise<void>;
}

const Ctx = createContext<AdminPermissionsContextValue>({
  loaded: false,
  admin: null,
  hasPermission: () => false,
  refresh: async () => {},
});

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export function AdminPermissionsProvider({ children }: { children: ReactNode }) {
  const apiUrl = useApiUrl();
  const [admin, setAdmin] = useState<CurrentAdmin | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);

  const load = useCallback(async () => {
    try {
      const resp = await adminFetch(`${apiUrl}/v1/admin/me`);
      const data: CurrentAdmin = await resp.json();
      setAdmin(data);
      setLastFetchedAt(Date.now());
    } catch (_err) {
      // adminFetch throws on 401/non-2xx. The user is unauthenticated
      // (or the API is unreachable) — clear the profile and let auth
      // provider redirect to /login.
      setAdmin(null);
    } finally {
      setLoaded(true);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
  }, [load]);

  // Refetch on window focus if cache is stale. Catches the case where
  // a super_admin grants/revokes the current admin's roles from another
  // tab — no full reload needed.
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastFetchedAt > CACHE_TTL_MS) {
        load();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [lastFetchedAt, load]);

  const hasPermission = useCallback(
    (key: string) => Boolean(admin?.permissions.includes(key)),
    [admin],
  );

  return (
    <Ctx.Provider value={{ loaded, admin, hasPermission, refresh: load }}>
      {children}
    </Ctx.Provider>
  );
}

/**
 * Hook to read the current admin + check permissions.
 * Safe to call before the initial fetch resolves — `loaded` is false
 * during that window and `hasPermission` returns false.
 */
export function useAdminPermissions(): AdminPermissionsContextValue {
  return useContext(Ctx);
}

export type { CurrentAdmin };
